import { useState, useEffect, useRef, useCallback } from 'react';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch, ApiError } from '@/lib/api';
import { DAY_DIARY_COPY } from '@/lib/dayDiaryCopy';
import type { DiaryProjectBatchStatus, DiarySuggestion, DiaryStatus } from '@/types/dayDiaryV2';
import {
  DAY_DIARY_V2_BATCHES_IN_FLIGHT,
  DAY_DIARY_V2_BATCHES_COMPLETE,
  DAY_DIARY_V2_SUGGESTIONS,
} from '@/data/dayDiaryV2';

export interface AttributedEventItem {
  id: string;
  occurredAt: string;
  app: string | null;
  title: string | null;
  site: string | null;
  classifiedClientName: string | null;
  classifiedProjectName: string | null;
}

export interface DayDiaryEntry {
  id: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  projectId: string | null;
  projectName: string | null;
  clientName: string | null;
  confidence: number;
  entryType: string;
  eventCount: number;
  eventItems: AttributedEventItem[];
}

export interface UnattributedEventItem {
  id: string;
  occurredAt: string;
  app: string | null;
  title: string | null;
  site: string | null;
  classifiedClientName: string | null;
  classifiedProjectName: string | null;
}

export interface DiarySummaryBlock {
  label: string;
  totalMinutes: number;
  narrative: string;
  entryIds: string[];
}

export interface DiarySummaryClient {
  clientName: string;
  totalMinutes: number;
  blocks: DiarySummaryBlock[];
}

export interface DiarySummary {
  clients: DiarySummaryClient[];
  mindShare: string;
}

export interface DayDiaryData {
  id: string;
  orgId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  status: string;
  modelVersion: string | null;
  generatedAt: string | null;
  entries: DayDiaryEntry[];
  totalEvents: number;
  workEventCount: number;
  personalEventCount: number;
  breakEventCount: number;
  unattributedEvents: number;
  unattributedEventItems: UnattributedEventItem[];
  rawSummary: DiarySummary | null;
}

interface UseDayDiaryResult {
  data: DayDiaryData | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  generate: (employeeId: string, date: string, force?: boolean) => Promise<void>;
  lookup: (employeeId: string, date: string) => Promise<void>;
}

const POLL_INTERVAL_MS = 3000;

export function useDayDiary(): UseDayDiaryResult {
  const { currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const [data, setData] = useState<DayDiaryData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const lookup = async (employeeId: string, date: string) => {
    stopPolling();
    setIsGenerating(false);
    setIsLoading(true);
    setError(null);
    setData(null);
    try {
      const token = await getToken();
      const result = await apiFetch<DayDiaryData>(
        `/api/v1/orgs/${orgId}/reports/day-diary?employeeId=${encodeURIComponent(employeeId)}&date=${encodeURIComponent(date)}`,
        token,
      );
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // no diary yet — not an error, just nothing to show
        setData(null);
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const generate = async (employeeId: string, date: string, force = false) => {
    stopPolling();
    setIsGenerating(true);
    setError(null);

    // Capture pre-generation timestamp so the poll can detect when a new
    // result replaces an existing one (handles the regeneration path).
    const previousGeneratedAt = data?.generatedAt ?? null;
    setData(null);

    const token = await getToken();

    // Fire POST without awaiting. The server generates synchronously; the
    // poll below picks up the result once the DB row is written.
    const url = `/api/v1/orgs/${orgId}/reports/day-diary${force ? '?force=true' : ''}`;
    apiFetch<DayDiaryData>(url, token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeId, date }),
    }).catch((err: unknown) => {
      stopPolling();
      setIsGenerating(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Generation failed');
      }
    });

    // Poll GET every 3 s until a fresh diary appears. Stop when generatedAt
    // differs from the value captured before the POST fired.
    pollRef.current = setInterval(() => {
      void (async () => {
        try {
          const pollToken = await getToken();
          const result = await apiFetch<DayDiaryData>(
            `/api/v1/orgs/${orgId}/reports/day-diary?employeeId=${encodeURIComponent(employeeId)}&date=${encodeURIComponent(date)}`,
            pollToken,
          );
          if (result.generatedAt !== previousGeneratedAt) {
            stopPolling();
            setData(result);
            setIsGenerating(false);
          }
        } catch (pollErr) {
          if (pollErr instanceof ApiError && pollErr.status === 404) {
            // Not ready yet — keep polling.
          } else {
            stopPolling();
            setIsGenerating(false);
          }
        }
      })();
    }, POLL_INTERVAL_MS);
  };

  return { data, isLoading, isGenerating, error, generate, lookup };
}

// ───────────────────────────────────────────────────────────────────────────
// Day Diary v2 (task 237, D3)
// ───────────────────────────────────────────────────────────────────────────

/**
 * The GET/POST `/day-diary-v2` response shape (task 235): the diary header +
 * an array of per-`DayDiaryProjectBatch` status rows. The frontend polls GET
 * while `status != 'complete'`.
 *
 * NOTE: the v2 GET route returns `{ diary, batches }` but does NOT expose the
 * fallback-batch reclamation suggestions in its payload. Until a suggestion-
 * list route lands, the live `suggestions` array is empty; the deterministic
 * fixture set is surfaced in mockup mode (the established frontend pattern,
 * also what the component tests + a11y gate exercise). The `useDayDiaryV2`
 * caller renders the cards from whichever source is populated.
 */
export interface DayDiaryV2Response {
  diary: {
    id: string;
    employeeId: string;
    employeeName: string;
    date: string;
    status: DiaryStatus;
    generatedAt: string | null;
  };
  batches: DiaryProjectBatchStatus[];
}

export interface UseDayDiaryV2Result {
  status: DiaryStatus | null;
  batches: DiaryProjectBatchStatus[];
  suggestions: DiarySuggestion[];
  isLoading: boolean;
  isComplete: boolean;
  error: string | null;
  /** POST `/day-diary-v2` (optionally `?force=true`) then poll until complete. */
  generate: (employeeId: string, date: string, force?: boolean) => Promise<void>;
  /** GET `/day-diary-v2`, starting a poll if the diary is non-complete. */
  lookup: (employeeId: string, date: string) => Promise<void>;
  /** POST task 236's accept route, then refresh (AC4/AC5/AC6). */
  accept: (suggestion: DiarySuggestion) => Promise<void>;
  /** POST task 236's dismiss route, then refresh (AC4). */
  dismiss: (suggestion: DiarySuggestion, reason?: string) => Promise<void>;
  /** Suggestion id currently mutating (disables that card's actions). */
  busyId: string | null;
}

/**
 * Day Diary v2 data hook. POST/GET against `/day-diary-v2` (task 235), polling
 * while the diary status is non-complete; accept/dismiss mutate task 236's
 * routes and refresh. Task 239 reclaims the `/day-diary-v2` URL to `/day-diary`
 * at cutover — change the four URLs here in that PR.
 */
export function useDayDiaryV2(): UseDayDiaryV2Result {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const [status, setStatus] = useState<DiaryStatus | null>(null);
  const [batches, setBatches] = useState<DiaryProjectBatchStatus[]>([]);
  const [suggestions, setSuggestions] = useState<DiarySuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastQuery = useRef<{ employeeId: string; date: string } | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const applyResponse = useCallback(
    (res: DayDiaryV2Response) => {
      setStatus(res.diary.status);
      setBatches(res.batches);
      // Live route does not yet expose suggestions; mockup mode supplies the
      // fixture set once the diary is complete so the panel renders.
      if (mockupMode) {
        setSuggestions(res.diary.status === 'complete' ? [...DAY_DIARY_V2_SUGGESTIONS] : []);
      }
    },
    [mockupMode],
  );

  const fetchV2 = useCallback(
    async (employeeId: string, date: string): Promise<DayDiaryV2Response | null> => {
      if (mockupMode) {
        // Deterministic fixture: complete diary with the three batches.
        return {
          diary: {
            id: 'diary-v2-mockup',
            employeeId,
            employeeName: 'Avery Chen',
            date,
            status: 'complete',
            generatedAt: `${date}T17:00:00.000Z`,
          },
          batches: [...DAY_DIARY_V2_BATCHES_COMPLETE],
        };
      }
      const token = await getToken();
      try {
        return await apiFetch<DayDiaryV2Response>(
          `/api/v1/orgs/${orgId}/reports/day-diary-v2?employeeId=${encodeURIComponent(employeeId)}&date=${encodeURIComponent(date)}`,
          token,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    [mockupMode, getToken, orgId],
  );

  const startPolling = useCallback(
    (employeeId: string, date: string) => {
      stopPolling();
      pollRef.current = setInterval(() => {
        void (async () => {
          try {
            const res = await fetchV2(employeeId, date);
            if (res) {
              applyResponse(res);
              if (res.diary.status === 'complete' || res.diary.status === 'failed') {
                stopPolling();
              }
            }
          } catch {
            stopPolling();
          }
        })();
      }, POLL_INTERVAL_MS);
    },
    [stopPolling, fetchV2, applyResponse],
  );

  const lookup = useCallback(
    async (employeeId: string, date: string) => {
      stopPolling();
      lastQuery.current = { employeeId, date };
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchV2(employeeId, date);
        if (res) {
          applyResponse(res);
          if (res.diary.status !== 'complete' && res.diary.status !== 'failed') {
            startPolling(employeeId, date);
          }
        } else {
          setStatus(null);
          setBatches([]);
          setSuggestions([]);
        }
      } catch {
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [stopPolling, fetchV2, applyResponse, startPolling],
  );

  const generate = useCallback(
    async (employeeId: string, date: string, force = false) => {
      stopPolling();
      lastQuery.current = { employeeId, date };
      setError(null);
      setIsLoading(true);

      if (mockupMode) {
        // No backend round-trip — show the in-flight batches, then settle.
        setStatus('fanned_out');
        setBatches([...DAY_DIARY_V2_BATCHES_IN_FLIGHT]);
        setSuggestions([]);
        const res = await fetchV2(employeeId, date);
        if (res) applyResponse(res);
        setIsLoading(false);
        return;
      }

      const token = await getToken();
      const url = `/api/v1/orgs/${orgId}/reports/day-diary-v2${force ? '?force=true' : ''}`;
      try {
        const res = await apiFetch<DayDiaryV2Response>(url, token, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ employeeId, date, ...(force ? { force: true } : {}) }),
        });
        applyResponse(res);
        if (res.diary.status !== 'complete' && res.diary.status !== 'failed') {
          startPolling(employeeId, date);
        }
      } catch (err) {
        // 409 force-while-in-flight uses the assistant-voice copy verbatim.
        if (err instanceof ApiError && err.status === 409) {
          setError(DAY_DIARY_COPY.DAY_DIARY_IN_FLIGHT);
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Generation failed');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [stopPolling, mockupMode, fetchV2, applyResponse, getToken, orgId, startPolling],
  );

  const refresh = useCallback(async () => {
    if (!lastQuery.current) return;
    const res = await fetchV2(lastQuery.current.employeeId, lastQuery.current.date);
    if (res) applyResponse(res);
  }, [fetchV2, applyResponse]);

  const mutateSuggestion = useCallback(
    async (
      suggestion: DiarySuggestion,
      action: 'accept' | 'dismiss',
      reason?: string,
    ): Promise<void> => {
      setBusyId(suggestion.id);
      setError(null);
      try {
        if (!mockupMode) {
          const token = await getToken();
          await apiFetch(
            `/api/v1/orgs/${orgId}/reports/diary-suggestions/${encodeURIComponent(suggestion.id)}/${action}`,
            token,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(action === 'dismiss' && reason ? { reason } : {}),
            },
          );
        }
        // Optimistically drop the settled suggestion from the panel; the next
        // poll/refresh re-derives the authoritative list (AC4/AC5).
        setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
        await refresh();
      } catch (err) {
        // PersonalProjectMissingError (500) surfaces the assistant-voice copy.
        if (err instanceof ApiError && err.status === 500) {
          setError(DAY_DIARY_COPY.PERSONAL_PROJECT_MISCONFIG);
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
      } finally {
        setBusyId(null);
      }
    },
    [mockupMode, getToken, orgId, refresh],
  );

  const accept = useCallback(
    (suggestion: DiarySuggestion) => mutateSuggestion(suggestion, 'accept'),
    [mutateSuggestion],
  );
  const dismiss = useCallback(
    (suggestion: DiarySuggestion, reason?: string) =>
      mutateSuggestion(suggestion, 'dismiss', reason),
    [mutateSuggestion],
  );

  return {
    status,
    batches,
    suggestions,
    isLoading,
    isComplete: status === 'complete',
    error,
    generate,
    lookup,
    accept,
    dismiss,
    busyId,
  };
}
